"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { createClient } from "@/lib/supabase/client";
import FeedbackModal from "@/components/FeedbackModal";

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const NAV_ITEMS = [
  { icon: "home", label: "Accueil", href: "/", disabled: false },
  { icon: "add_photo_alternate", label: "Nouvelle création", href: "/upload", disabled: false },
  { icon: "history", label: "Historique", href: "/history", disabled: false },
  { icon: "person", label: "Profil", href: "/profile", disabled: false },
  { icon: "auto_awesome", label: "Conseils style & SEO", href: "#", disabled: true },
  {
    icon: "rate_review",
    label: "Donner mon avis",
    href: "#",
    disabled: false,
    action: "feedback" as const,
  },
];

export default function MenuDrawer({ isOpen, onClose }: MenuDrawerProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setAvatarUrl(user.user_metadata?.avatar_url ?? null);
        setUserName(
          user.user_metadata?.full_name ??
            user.user_metadata?.name ??
            user.email?.split("@")[0] ??
            null
        );
      }
    });
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`bg-background fixed inset-y-0 left-0 z-[70] w-[85%] max-w-[320px] transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } flex flex-col overflow-y-auto`}
      >
        {/* Header with profile */}
        <header className="border-outline-variant/15 border-b px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-5">
          <div className="mb-5 flex items-center justify-between">
            <h1 className="font-headline text-on-surface text-xl italic">Vitrine</h1>
            <button
              onClick={onClose}
              className="bg-surface-container-high/50 flex h-8 w-8 items-center justify-center rounded-full transition-transform active:scale-90"
              aria-label="Fermer le menu"
            >
              <span className="material-symbols-outlined text-on-surface-variant text-lg">
                close
              </span>
            </button>
          </div>

          {/* Profile row */}
          <div className="flex items-center gap-3">
            <div className="bg-primary-fixed relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-full">
              {avatarUrl ? (
                <Image
                  alt="Photo de profil"
                  className="object-cover"
                  fill
                  sizes="40px"
                  src={avatarUrl}
                />
              ) : (
                <span
                  className="material-symbols-outlined text-primary text-xl"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  person
                </span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-on-surface truncate text-sm font-semibold">
                {userName ?? "Mon compte"}
              </p>
              <Link
                href="/profile"
                onClick={onClose}
                className="text-on-surface-variant hover:text-primary text-xs transition-colors"
              >
                Voir le profil
              </Link>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3">
          <p className="text-on-surface-variant font-label mb-2 px-3 text-[10px] tracking-widest uppercase">
            Navigation
          </p>
          {NAV_ITEMS.map((item) => {
            const content = (
              <div
                className={`flex items-center gap-3 rounded-xl px-3 py-3 transition-colors ${
                  item.disabled
                    ? "cursor-not-allowed opacity-35"
                    : "hover:bg-surface-container-low active:bg-surface-container"
                }`}
              >
                <span
                  className="material-symbols-outlined text-on-surface-variant text-xl"
                  style={!item.disabled ? { fontVariationSettings: "'FILL' 1" } : undefined}
                >
                  {item.icon}
                </span>
                <span className="text-on-surface text-sm font-medium">{item.label}</span>
                {item.disabled && (
                  <span className="text-on-surface-variant/60 bg-surface-container-high/50 ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium">
                    Bientôt
                  </span>
                )}
              </div>
            );

            if (item.disabled) {
              return <div key={item.label}>{content}</div>;
            }

            if ("action" in item && item.action === "feedback") {
              return (
                <button
                  key={item.label}
                  onClick={() => {
                    onClose();
                    setTimeout(() => setFeedbackOpen(true), 300);
                  }}
                  className="w-full text-left"
                >
                  {content}
                </button>
              );
            }

            return (
              <Link key={item.label} href={item.href} onClick={onClose}>
                {content}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <footer className="border-outline-variant/15 mt-auto border-t px-5 py-4">
          <button
            onClick={async () => {
              await fetch("/api/auth/logout", { method: "POST" });
              window.location.href = "/login";
            }}
            className="text-on-surface-variant flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm transition-colors hover:bg-red-50 hover:text-red-600 active:scale-[0.98]"
          >
            <span className="material-symbols-outlined text-lg">logout</span>
            Déconnexion
          </button>
        </footer>
      </div>

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}
